import type { BasicQuestion } from "@repo/app-types/client/client";
import type { InterviewSectionDefinition } from "@repo/app-types/entities/interviewSection/definition";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Label } from "@repo/ui/Label/label";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { Size } from "@repo/ui/helpers/helpers";
import { IconRefresh } from "@tabler/icons-react";
import type { FC } from "react";
import { OverviewBox } from "../../../../components/overviewBox.js";
import { ReadOnlyBulletedList } from "../../../../components/readOnlyBulletedList.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { renderInterviewSectionBlurb } from "../../../interviewSections/definitions/helpers.js";

const NUM_QUESTIONS_TO_DISPLAY = 4;

/** Used to preview the interview sections on the project detail/settings page. */
interface RecruitingInterviewSectionsOverviewProps {
  questions: BasicQuestion[];
  interviewSections: InterviewSectionDefinition[];
  isDisabled: boolean;
  isLoading: boolean;
  isFailed: boolean;
  onRetryClick: () => void;
  onEditClick: () => void;
}

export const RecruitingInterviewSectionsOverview: FC<
  RecruitingInterviewSectionsOverviewProps
> = ({
  questions,
  interviewSections = [],
  isDisabled,
  isLoading,
  isFailed,
  onRetryClick,
  onEditClick,
}) => {
  const listItems = getListItems(questions, interviewSections);

  return (
    <div>
      <Label size={Size.SMALL} className="mb-2">
        AI interview
      </Label>
      {listItems.length > 0 ? (
        <OverviewBox isDisabled={isDisabled} onClick={onEditClick}>
          <ReadOnlyBulletedList
            items={listItems}
            maxItemsToDisplay={NUM_QUESTIONS_TO_DISPLAY}
            itemDescriptor="questions"
          />
        </OverviewBox>
      ) : isLoading ? (
        <div className="flex items-center space-x-2 text-sm text-foreground">
          <span className="size-6 shrink-0">
            <Loader style={LoaderStyle.ORBIT} />
          </span>
          <span>
            Your questions are being generated. This may take a minute.
          </span>
        </div>
      ) : isFailed ? (
        <div className="flex items-center space-x-2 text-sm text-foreground">
          <IconButton
            variant={ButtonVariant.Tertiary}
            icon={<IconRefresh className="size-4 shrink-0" />}
            onClick={onRetryClick}
          />
          <span>An error occurred while generating your questions.</span>
        </div>
      ) : (
        <div className="mt-2">
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Primary}
                onClick={onEditClick}
                isDisabled={!allowed}
              >
                Add interview questions
              </Button>
            )}
          </Can>
        </div>
      )}
    </div>
  );
};

const getListItems = (
  questions: BasicQuestion[],
  interviewSections: InterviewSectionDefinition[],
) => {
  // If we only have a screening interview, show the questions
  if (questions.length > 0 && interviewSections.length === 0) {
    return questions.map((question) => question.question);
  }

  // If we have interview sections, show the sections
  return interviewSections.map((section) =>
    renderInterviewSectionBlurb(section, questions.length),
  );
};
